import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import {
  MaximizeIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
} from '../../Icons/DashboardIcons';

// Import refactored chart components
import PeakHoursChart from './charts/PeakHoursChart';
import DepartmentEngagementChart from './charts/DepartmentEngagementChart';
import YearlyEngagementChart from './charts/YearlyEngagementChart';
import ActiveUsersStatusBox from './charts/ActiveUsersStatusBox';

// Import reusable components
import { FullscreenChartModal, ErrorDisplay, SampleDataBadge } from './common';

// Import API
import TLDashboardAPIs from '../../utilities/api/dashboard/TLDashboardAPIs';

// Import sample data utilities
import { isDataEmpty, getSampleDataForPeriod } from '../../utilities/sampleData/tlDashboardSampleData';

const EngagementTab = ({ selectedPeriod = 'weekly' }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [maximizedChart, setMaximizedChart] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Fetch data when period or refreshTrigger changes
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await TLDashboardAPIs.getEngagement(selectedPeriod);
        setData(response);
      } catch (err) {
        console.error('Error fetching engagement data:', err);
        setError('Failed to load engagement data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedPeriod, refreshTrigger]);

  const handleRetry = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          gap: 2
        }}
      >
        <CircularProgress size={48} sx={{ color: '#007bff' }} />
        <Typography sx={{ color: '#666', fontSize: '14px', fontWeight: 500 }}>
          Fetching engagement data...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return <ErrorDisplay message={error} onRetry={handleRetry} />;
  }

  // Extract data from API response with sample data fallback
  const rawPeakHours = data?.peak_hours_analysis || [];
  const isPeakHoursEmpty = isDataEmpty(rawPeakHours, 'array');
  const peakHoursAnalysis = isPeakHoursEmpty ? getSampleDataForPeriod('peakHoursAnalysis') : rawPeakHours;

  const rawActiveUsers = data?.active_users_status || {
    active_users: 0,
    inactive_users: 0,
    active_percentage: '0%',
    inactive_percentage: '0%'
  };
  const isActiveUsersEmpty = rawActiveUsers.active_users === 0 && rawActiveUsers.inactive_users === 0;
  const activeUsersStatus = isActiveUsersEmpty ? getSampleDataForPeriod('activeUsersStatus') : rawActiveUsers;

  const rawDeptEngagement = data?.department_engagement || [];
  const isDeptEngagementEmpty = isDataEmpty(rawDeptEngagement, 'array');
  const departmentEngagement = isDeptEngagementEmpty ? getSampleDataForPeriod('departmentEngagement') : rawDeptEngagement;

  const rawYearEngagement = data?.year_engagement || [];
  const isYearEngagementEmpty = isDataEmpty(rawYearEngagement, 'array');
  const yearEngagement = isYearEngagementEmpty ? getSampleDataForPeriod('yearEngagement') : rawYearEngagement;

  // Helper function to get chart title
  const getChartTitle = (chartType) => {
    switch (chartType) {
      case 'peakHours': return 'Peak Hours Analysis';
      case 'activeUsers': return 'Active Users Status';
      case 'departmentEngagement': return 'Department-wise Engagement';
      case 'yearEngagement': return 'Year-wise Engagement';
      default: return '';
    }
  };

  // Helper function to render chart content in fullscreen
  const renderChartContent = (chartType) => {
    switch (chartType) {
      case 'peakHours':
        return (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <PeakHoursChart data={peakHoursAnalysis} />
            </Box>
          </Box>
        );
      case 'activeUsers':
        return (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '24px',
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                height: '70%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ActiveUsersStatusBox
                activeUsers={activeUsersStatus.active_users}
                inactiveUsers={activeUsersStatus.inactive_users}
                activePercentage={activeUsersStatus.active_percentage}
                inactivePercentage={activeUsersStatus.inactive_percentage}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: '40px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Box
                  sx={{
                    width: '14px',
                    height: '14px',
                    borderRadius: '50%',
                    bgcolor: '#007bff',
                  }}
                />
                <Typography
                  sx={{
                    fontSize: '18px',
                    fontWeight: 500,
                    color: '#666666',
                    fontFamily: '"Inter", sans-serif',
                  }}
                >
                  Active ({activeUsersStatus.active_users})
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Box
                  sx={{
                    width: '14px',
                    height: '14px',
                    borderRadius: '50%',
                    bgcolor: '#a6a6a6',
                  }}
                />
                <Typography
                  sx={{
                    fontSize: '18px',
                    fontWeight: 500,
                    color: '#666666',
                    fontFamily: '"Inter", sans-serif',
                  }}
                >
                  Inactive ({activeUsersStatus.inactive_users})
                </Typography>
              </Box>
            </Box>
          </Box>
        );
      case 'departmentEngagement':
        return (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <DepartmentEngagementChart data={departmentEngagement} />
            </Box>
          </Box>
        );
      case 'yearEngagement':
        return (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
              {yearEngagement.length > 0 ? (
                <YearlyEngagementChart data={yearEngagement} />
              ) : (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                }}>
                  <Typography
                    sx={{
                      fontSize: '16px',
                      color: '#999999',
                      fontFamily: '"Inter", sans-serif',
                    }}
                  >
                    No year-wise engagement data available
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Box>
      {/* Fullscreen Chart Modal */}
      <FullscreenChartModal
        open={Boolean(maximizedChart)}
        onClose={() => setMaximizedChart(null)}
        title={getChartTitle(maximizedChart)}
      >
        {renderChartContent(maximizedChart)}
      </FullscreenChartModal>
      {/* First Row */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: { xs: '16px', md: '24px' },
        }}
      >
        {/* Peak Hours Analysis */}
        <Box
          sx={{
            bgcolor: '#ffffff',
            borderRadius: { xs: '8px', md: '12px' },
            border: '1px solid #e6e6e6',
            boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.05)',
            overflow: 'hidden',
            height: { xs: '320px', sm: '360px', md: '408px' },
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              padding: { xs: '10px', md: '12px' },
              borderBottom: '1px solid #e6e6e6',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Typography
                sx={{
                  fontSize: { xs: '14px', md: '16px' },
                  fontWeight: 500,
                  fontFamily: '"Inter", sans-serif',
                  color: '#333333',
                }}
              >
                Peak Hours Analysis
              </Typography>
              <SampleDataBadge show={isPeakHoursEmpty} absolute={false} />
            </Box>
            <Box
              onClick={() => setMaximizedChart('peakHours')}
              sx={{
                padding: { xs: '6px', md: '8px' },
                border: '1px solid #e6e6e6',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: '#f5f5f5',
                  borderColor: '#f99f24',
                },
              }}
            >
              <MaximizeIcon />
            </Box>
          </Box>
          <Box
            sx={{
              padding: { xs: '10px', md: '12px' },
              flex: 1,
              minHeight: 0,
              display: 'flex',
            }}
          >
            <Box
              sx={{
                flex: 1,
                border: '1px solid #e0e0e0',
                borderRadius: '20px',
                padding: { xs: '12px', md: '20px' },
                display: 'flex',
                flexDirection: 'column',
                bgcolor: '#ffffff',
              }}
            >
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <PeakHoursChart data={peakHoursAnalysis} />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: '8px',
                  mt: { xs: 1, md: 2 },
                }}
              >
                <Box
                  sx={{
                    padding: '4px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    bgcolor: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: '#f5f5f5' }
                  }}
                >
                  <ArrowLeftIcon />
                </Box>
                <Box
                  sx={{
                    padding: '4px 12px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    bgcolor: '#ffffff',
                  }}
                >
                  <Typography sx={{ fontSize: '12px', color: '#666666', fontWeight: 500, fontFamily: '"Inter", sans-serif' }}>
                    {data?.period_start && data?.period_end
                      ? `${new Date(data.period_start).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' })} - ${new Date(data.period_end).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' })}`
                      : '01/02'}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    padding: '4px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    bgcolor: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: '#f5f5f5' }
                  }}
                >
                  <ArrowRightIcon />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Active Users Status */}
        <Box
          sx={{
            bgcolor: '#ffffff',
            borderRadius: { xs: '8px', md: '12px' },
            border: '1px solid #e6e6e6',
            boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.05)',
            overflow: 'hidden',
            height: { xs: '320px', sm: '360px', md: '408px' },
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              padding: { xs: '10px', md: '12px' },
              borderBottom: '1px solid #e6e6e6',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Typography
                sx={{
                  fontSize: { xs: '14px', md: '16px' },
                  fontWeight: 500,
                  fontFamily: '"Inter", sans-serif',
                  color: '#333333',
                }}
              >
                Active Users Status
              </Typography>
              <SampleDataBadge show={isActiveUsersEmpty} absolute={false} />
            </Box>
            <Box
              onClick={() => setMaximizedChart('activeUsers')}
              sx={{
                padding: { xs: '6px', md: '8px' },
                border: '1px solid #e6e6e6',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: '#f5f5f5',
                  borderColor: '#007bff',
                },
              }}
            >
              <MaximizeIcon />
            </Box>
          </Box>
          <Box
            sx={{
              padding: { xs: '10px', md: '12px' },
              flex: 1,
              minHeight: 0,
              display: 'flex',
            }}
          >
            <Box
              sx={{
                flex: 1,
                border: '1px solid #e0e0e0',
                borderRadius: '20px',
                padding: { xs: '12px', md: '20px' },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: { xs: '16px', md: '24px' },
                bgcolor: '#ffffff',
              }}
            >
              <ActiveUsersStatusBox
                activeUsers={activeUsersStatus.active_users}
                inactiveUsers={activeUsersStatus.inactive_users}
                activePercentage={activeUsersStatus.active_percentage}
                inactivePercentage={activeUsersStatus.inactive_percentage}
              />
              <Box
                sx={{
                  display: 'flex',
                  gap: { xs: '20px', md: '40px' },
                  pb: 1
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Box
                    sx={{
                      width: { xs: '10px', md: '12px' },
                      height: { xs: '10px', md: '12px' },
                      borderRadius: '50%',
                      bgcolor: '#007bff',
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: { xs: '14px', md: '16px' },
                      fontWeight: 500,
                      color: '#666666',
                      fontFamily: '"Inter", sans-serif',
                    }}
                  >
                    Active ({activeUsersStatus.active_users})
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Box
                    sx={{
                      width: { xs: '10px', md: '12px' },
                      height: { xs: '10px', md: '12px' },
                      borderRadius: '50%',
                      bgcolor: '#a6a6a6',
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: { xs: '14px', md: '16px' },
                      fontWeight: 500,
                      color: '#666666',
                      fontFamily: '"Inter", sans-serif',
                    }}
                  >
                    Inactive ({activeUsersStatus.inactive_users})
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Second Row */}
      <Box
        sx={{
          mt: { xs: 2, md: 3 },
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: { xs: '16px', md: '24px' },
        }}
      >
        {/* Department-wise Engagement */}
        <Box
          sx={{
            bgcolor: '#ffffff',
            borderRadius: { xs: '8px', md: '12px' },
            border: '1px solid #e6e6e6',
            boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.05)',
            overflow: 'hidden',
            height: { xs: '300px', sm: '350px', md: '408px' },
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              padding: { xs: '10px', md: '12px' },
              borderBottom: '1px solid #e6e6e6',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Typography
                sx={{
                  fontSize: { xs: '14px', md: '16px' },
                  fontWeight: 500,
                  fontFamily: '"Inter", sans-serif',
                  color: '#333333',
                }}
              >
                Department-wise Engagement
              </Typography>
              <SampleDataBadge show={isDeptEngagementEmpty} absolute={false} />
            </Box>
            <Box
              onClick={() => setMaximizedChart('departmentEngagement')}
              sx={{
                padding: { xs: '6px', md: '8px' },
                border: '1px solid #e6e6e6',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: '#f5f5f5',
                  borderColor: '#12ab0a',
                },
              }}
            >
              <MaximizeIcon />
            </Box>
          </Box>
          <Box
            sx={{
              padding: { xs: '10px', md: '12px' },
              flex: 1,
              minHeight: 0,
              display: 'flex',
            }}
          >
            <Box
              sx={{
                flex: 1,
                border: '1px solid #e0e0e0',
                borderRadius: '20px',
                padding: { xs: '12px', md: '20px' },
                display: 'flex',
                bgcolor: '#ffffff',
              }}
            >
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <DepartmentEngagementChart data={departmentEngagement} />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Year-wise Engagement */}
        <Box
          sx={{
            bgcolor: '#ffffff',
            borderRadius: { xs: '8px', md: '12px' },
            border: '1px solid #e6e6e6',
            boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.05)',
            overflow: 'hidden',
            height: { xs: '300px', sm: '350px', md: '408px' },
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              padding: { xs: '10px', md: '12px' },
              borderBottom: '1px solid #e6e6e6',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Typography
                sx={{
                  fontSize: { xs: '14px', md: '16px' },
                  fontWeight: 500,
                  fontFamily: '"Inter", sans-serif',
                  color: '#333333',
                }}
              >
                Year-wise Engagement
              </Typography>
              <SampleDataBadge show={isYearEngagementEmpty} absolute={false} />
            </Box>
            <Box
              onClick={() => setMaximizedChart('yearEngagement')}
              sx={{
                padding: { xs: '6px', md: '8px' },
                border: '1px solid #e6e6e6',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: '#f5f5f5',
                  borderColor: '#c144ff',
                },
              }}
            >
              <MaximizeIcon />
            </Box>
          </Box>
          <Box
            sx={{
              padding: { xs: '10px', md: '12px' },
              flex: 1,
              minHeight: 0,
              display: 'flex',
            }}
          >
            <Box
              sx={{
                flex: 1,
                border: '1px solid #e0e0e0',
                borderRadius: '20px',
                padding: { xs: '12px', md: '20px' },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: '#ffffff',
              }}
            >
              {yearEngagement.length > 0 ? (
                <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
                  <YearlyEngagementChart data={yearEngagement} />
                </Box>
              ) : (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  width: '100%'
                }}>
                  <Typography
                    sx={{
                      fontSize: '14px',
                      color: '#999999',
                      fontFamily: '"Inter", sans-serif',
                    }}
                  >
                    No year-wise engagement data available
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default EngagementTab;
